import { useEffect, useRef, useState } from 'react';
import { ArrowRight, CircleNotch, FilePdf, Image as ImageIcon, PaperPlaneTilt, Plus } from '@phosphor-icons/react';
import { useSearchParams, useNavigate } from '../lib/routing';
import { HeaderContent, HEADER_TITLE_CLASS, APP_HEADER_HEIGHT_VAR } from '../components/shell/app-header';
import Markdown from '../components/markdown';
import DocumentDrawer from '../components/document-drawer';
import { ToolBlockList } from '../components/tool-block';
import WidgetFrame from '../components/widget-frame';
import TypingDots from '../components/typing-dots';
import {
  listTeacherMessages,
  streamTeacherChat,
  uploadTeacherAttachment,
  type DocRef,
  type TeacherChatAttachment,
  type TeacherMsg,
  type TeacherWidget,
  type ToolCall,
  type WebSource,
} from '../lib/editor-chat';
import { useChatAttachments } from './chat/use-chat-attachments';
import { useSendLock } from './chat/use-send-lock';
import { PendingThumb } from './chat/pending-thumb';
import { IMAGE_MIMES, isImageMime } from './chat/attachments';
import { AttachmentList } from './chat/attachment-list';
import { useWebVoice } from '../lib/use-web-voice';
import { VoiceMicButton, VoiceStatusBar } from '../components/voice-controls';
import { useRoles } from '../hooks/use-roles';
import { api } from '../lib/api';
import { onboardingHref } from '../lib/onboarding-return';
import { setUnsavedChanges } from '../lib/unsaved-changes';
import { Alert } from '@/components/ui/alert';
import { CHAT_SLOW_NOTICE, chatAttachmentErrorMessage, chatErrorMessage } from '../lib/chat-errors';
import { newClientSendId, planRetry } from '../lib/turn-recovery';
import { RetryAlert, SlowNotice } from '../components/chat-turn-notices';
import { cn } from '@/lib/utils';
import { useAutoGrowTextarea } from '../hooks/use-autogrow-textarea';
import { composerKeyHandler } from '../lib/composer-keys';

const COMPOSER_MAX_HEIGHT_PX = 168;

/* Portée resserrée à Ismael (urgence P1) : image ET PDF, pas les formats
   Office — le bucket les accepte mais aucune extraction de texte server-side
   n'existe pour eux (voir attachments.ts côté API). */
const PDF_MIME = 'application/pdf';

const SUGGESTIONS = [
  'Quels sont les attendus de fin de cycle 3 en français ?',
  'Comment adapter une leçon pour un élève avec TDAH ?',
  'Proposez-moi une progression sur les fractions pour la 5ᵉ',
  'Que dit le socle commun sur les compétences numériques ?',
];

const bubbleBase = 'max-w-[88%] rounded-card px-[14px] py-[11px] text-[15px] leading-[1.5] whitespace-pre-wrap break-words';

const HISTORY_WINDOW = 200;

export default function TeacherAssistant() {
  const navigate = useNavigate();
  const { activeRole, loading: rolesLoading } = useRoles();
  const [searchParams, setSearchParams] = useSearchParams();
  const threadId = searchParams.get('c');

  const [messages, setMessages] = useState<TeacherMsg[]>([]);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);
  const guardSend = useSendLock();
  const [streamText, setStreamText] = useState('');
  const [turnTools, setTurnTools] = useState<ToolCall[]>([]);
  const [turnWidgets, setTurnWidgets] = useState<TeacherWidget[]>([]);
  const [turnSources, setTurnSources] = useState<WebSource[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [slow, setSlow] = useState(false);
  const [partialText, setPartialText] = useState('');
  const failedTurnRef = useRef<{ text: string; uploaded: TeacherChatAttachment[]; knownBefore: number; clientSendId: string } | null>(null);
  const [drawer, setDrawer] = useState<{ docId: string; page: number } | null>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const loadedThreadRef = useRef<string | null>(null);
  const turnToolsRef = useRef<ToolCall[]>([]);
  const turnWidgetsRef = useRef<TeacherWidget[]>([]);
  const turnSourcesRef = useRef<WebSource[]>([]);
  const truncatedRef = useRef(false);
  const messagesRef = useRef<TeacherMsg[]>([]);
  messagesRef.current = messages;
  const imageInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const textareaRef = useAutoGrowTextarea(draft, COMPOSER_MAX_HEIGHT_PX);
  const { pending, pickFiles, removePending, clearPending } = useChatAttachments({
    disabled: streaming,
    onError: setError,
  });

  useEffect(() => {
    setUnsavedChanges('teacher-chat-draft', draft.trim().length > 0);
    return () => setUnsavedChanges('teacher-chat-draft', false);
  }, [draft]);

  useEffect(() => {
    if (rolesLoading || activeRole !== 'teacher') return;
    let cancelled = false;
    (async () => {
      try {
        const status = await api.onboarding.status.query();
        if (!cancelled && status && !status.onboarding_completed) {
          navigate(onboardingHref('/onboarding', '/teacher/chat'), { replace: true });
        }
      } catch { /* ignore — stay on page if status check fails */ }
    })();
    return () => { cancelled = true; };
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [rolesLoading, activeRole]);

  const voice = useWebVoice({
    threadId,
    mode: 'teacher',
    getHistory: () =>
      messagesRef.current
        .filter((m) => m.content && m.content.trim())
        .map((m) => ({ role: m.role === 'assistant' ? ('model' as const) : ('user' as const), text: m.content })),
    onAppend: (msgs) => setMessages((prev) => [...prev, ...msgs.map((m) => ({ role: m.role, content: m.content, widgets: m.widgets }))]),
    /* Le fil est créé par le proxy quand la conversation est neuve : sans cette
       adoption, ce qui vient d'être dit existe en base mais reste introuvable. */
    onThread: (id) => {
      loadedThreadRef.current = id;
      setSearchParams((prev) => { const n = new URLSearchParams(prev); n.set('c', id); return n; }, { replace: true });
    },
    onError: setError,
  });

  useEffect(() => {
    if (!threadId) {
      setMessages([]);
      loadedThreadRef.current = null;
      return;
    }
    if (threadId === loadedThreadRef.current) return;
    let cancelled = false;
    listTeacherMessages(threadId)
      .then((m) => {
        if (!cancelled) {
          setMessages(m);
          loadedThreadRef.current = threadId;
        }
      })
      .catch((e) => !cancelled && setError(chatErrorMessage(e)));
    return () => { cancelled = true; };
  }, [threadId]);

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, streamText, turnTools]);

  /* `streaming` monte AVANT le televersement, pas apres : sinon le bouton reste
     actif pendant tout l'envoi du fichier, et un second geste genere — et
     facture — une seconde reponse. Le verrou de useSendLock couvre en plus les
     gestes d'un meme tick, que ce drapeau d'etat manque. */
  const send = (e?: { preventDefault: () => void }) => {
    e?.preventDefault();
    const text = draft.trim();
    if ((!text && pending.length === 0) || streaming) return;
    guardSend(() => sendTurn(text));
  };

  const onComposerKeyDown = composerKeyHandler(() => {
    if (streaming || voice.voiceLive) return;
    formRef.current?.requestSubmit();
  });

  const sendTurn = async (text: string) => {
    setError(null);
    setStreaming(true);

    /* Upload sequentiel : une piece jointe en echec doit rester visible dans */
    /* l'erreur plutot que de disparaitre silencieusement du tour. */
    const uploaded: TeacherChatAttachment[] = [];
    for (const p of pending) {
      try {
        uploaded.push(await uploadTeacherAttachment(p.file));
      } catch (err) {
        setError(chatAttachmentErrorMessage(err));
        setStreaming(false);
        return;
      }
    }
    const onlyImages = uploaded.length > 0 && uploaded.every((a) => isImageMime(a.mime));
    const attachFallback = onlyImages ? 'Regarde cette image.' : 'Regarde ce document.';
    const sentText = text || attachFallback;

    setDraft('');
    clearPending();
    const clientSendId = newClientSendId();
    failedTurnRef.current = { text: sentText, uploaded, knownBefore: messagesRef.current.length, clientSendId };
    setMessages((m) => [...m, { role: 'user', content: sentText, attachments: uploaded.length ? uploaded : undefined }]);
    await runStream(sentText, uploaded, clientSendId);
  };

  const runStream = async (sentText: string, uploaded: TeacherChatAttachment[], clientSendId: string) => {
    setError(null);
    setStreaming(true);
    setSlow(false);
    setPartialText('');
    setStreamText('');
    turnToolsRef.current = [];
    setTurnTools([]);
    turnWidgetsRef.current = [];
    setTurnWidgets([]);
    turnSourcesRef.current = [];
    setTurnSources([]);
    truncatedRef.current = false;
    const ac = new AbortController();
    abortRef.current = ac;
    let acc = '';
    try {
      await streamTeacherChat(
        sentText,
        threadId,
        {
          onDelta: (t) => { acc += t; setStreamText(acc); },
          onSlow: () => setSlow(true),
          onActive: () => setSlow(false),
          onTool: (name, args) => {
            turnToolsRef.current = [...turnToolsRef.current, { name, args: args as ToolCall['args'] }];
            setTurnTools(turnToolsRef.current);
          },
          onToolResult: (name, refs, count, levels) => {
            const arr = [...turnToolsRef.current];
            for (let i = arr.length - 1; i >= 0; i--) {
              const t = arr[i];
              if (t && t.name === name && t.refs === undefined && t.itemCount === undefined) {
                arr[i] = { ...t, ...(refs.length ? { refs } : {}), ...(count != null ? { itemCount: count } : {}), ...(levels?.length ? { levels } : {}) };
                break;
              }
            }
            turnToolsRef.current = arr;
            setTurnTools(arr);
          },
          onWidget: (w) => {
            turnWidgetsRef.current = [...turnWidgetsRef.current, w];
            setTurnWidgets(turnWidgetsRef.current);
          },
          onWebSources: ({ sources }) => {
            const seen = new Set(turnSourcesRef.current.map((s) => s.uri));
            turnSourcesRef.current = [...turnSourcesRef.current, ...sources.filter((s) => !seen.has(s.uri))];
            setTurnSources(turnSourcesRef.current);
          },
          onThread: (id) => {
            if (id !== threadId) {
              loadedThreadRef.current = id;
              setSearchParams((prev) => {
                const n = new URLSearchParams(prev);
                n.set('c', id);
                return n;
              }, { replace: true });
            }
          },
          onRetract: () => { acc = ''; setStreamText(''); },
          onTruncated: () => { truncatedRef.current = true; },
          onDone: (full) => {
            const tools = turnToolsRef.current;
            const widgets = turnWidgetsRef.current;
            const sources = turnSourcesRef.current;
            const truncated = truncatedRef.current || undefined;
            setMessages((m) => [...m, { role: 'assistant', content: full || acc, tools: tools.length ? tools : undefined, widgets: widgets.length ? widgets : undefined, webSources: sources.length ? sources : undefined, truncated }]);
            setStreamText('');
            turnToolsRef.current = [];
            setTurnTools([]);
            turnWidgetsRef.current = [];
            setTurnWidgets([]);
            turnSourcesRef.current = [];
            setTurnSources([]);
          },
          onError: (err) => { setError(err.message); setPartialText(acc); },
        },
        ac.signal,
        null,
        null,
        uploaded.length ? uploaded : null,
        undefined,
        undefined,
        undefined,
        clientSendId,
      );
    } catch (e2) {
      /* Lève avant/hors du flux SSE (auth, validation, panne non gérée côté
         serveur) : contrairement à `onError` ci-dessus (déjà sanitisé par le
         serveur), ce message n'a pas été écrit pour l'utilisateur. */
      setError(chatErrorMessage(e2));
      setPartialText(acc);
    } finally {
      setStreaming(false);
      setSlow(false);
      abortRef.current = null;
    }
  };

  const retryTurn = () => {
    const failed = failedTurnRef.current;
    if (!failed || streaming) return;
    guardSend(async () => {
      setError(null);
      setStreaming(true);
      const plan = threadId
        ? await planRetry(
            async () => (await listTeacherMessages(threadId)).map((m) => ({ role: m.role, transcript: m.content, failed: m.failed, clientSendId: m.clientSendId })),
            failed.knownBefore,
            failed.text,
            HISTORY_WINDOW,
            failed.clientSendId,
          )
        : 'resend';
      if (plan === 'already-processed' && threadId) {
        const fresh = await listTeacherMessages(threadId);
        setMessages(fresh);
        setPartialText('');
        failedTurnRef.current = null;
        setStreaming(false);
        return;
      }
      await runStream(failed.text, failed.uploaded, failed.clientSendId);
    });
  };

  const sendSuggestion = (text: string) => {
    if (streaming || voice.voiceLive) return;
    setDraft(text);
  };

  const newConversation = () => {
    if (streaming) return;
    setMessages([]);
    setStreamText('');
    turnToolsRef.current = [];
    setTurnTools([]);
    turnWidgetsRef.current = [];
    setTurnWidgets([]);
    loadedThreadRef.current = null;
    setSearchParams((prev) => {
      const n = new URLSearchParams(prev);
      n.delete('c');
      return n;
    });
  };

  const isEmpty = messages.length === 0 && !streaming;

  return (
    <div
      className="flex flex-col overflow-hidden"
      style={{ height: `calc(100dvh - var(${APP_HEADER_HEIGHT_VAR}, 0px))` }}
    >
      <HeaderContent
        width="full"
        showBurger
        actions={
          <button
            type="button"
            onClick={newConversation}
            disabled={streaming}
            className="flex-none flex items-center gap-1.5 px-3 py-2 border-none rounded-card bg-primary text-primary-foreground font-extrabold text-[13px] cursor-pointer font-[inherit] shadow-card disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Plus size={14} weight="bold" />
            Nouvelle
          </button>
        }
      >
        <div className="min-w-0">
          <h1 className={HEADER_TITLE_CLASS}>Assistant programme</h1>
          <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">
            Programmes, ressources officielles, adaptation pédagogique
          </p>
        </div>
      </HeaderContent>

      {/* Message list */}
      <div ref={scrollerRef} className="flex-1 min-h-0 overflow-auto p-4">
        <div className="max-w-[760px] mx-auto flex flex-col gap-3">

          {isEmpty && (
            <div className="flex flex-col gap-4 pt-2">
              <p className="text-[14px] text-muted-foreground leading-relaxed">
                Posez une question sur les programmes, les attendus, le socle ou l'école inclusive.
                L'assistant lit la base documentaire officielle et répond avec ses sources.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => sendSuggestion(s)}
                    className="flex items-center justify-between gap-3 text-left px-4 py-3 rounded-card border border-border bg-card text-[13.5px] text-foreground hover:bg-accent hover:text-primary hover:border-primary/30 transition-colors cursor-pointer font-[inherit] leading-snug group"
                  >
                    <span>{s}</span>
                    <ArrowRight size={14} weight="regular" className="flex-shrink-0 opacity-30 group-hover:opacity-70 transition-opacity" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <MessageItem key={i} msg={m} onOpenDoc={(docId, page) => setDrawer({ docId, page })} />
          ))}

          {streaming && (
            <div className="self-start max-w-[88%] w-full flex flex-col gap-[6px]">
              <ToolBlockList
                tools={turnTools}
                liveFor={(_t, j) => j === turnTools.length - 1 && !streamText}
                onOpenDoc={(docId, page) => setDrawer({ docId, page })}
              />
              {turnWidgets.map((w, j) => (
                <WidgetFrame key={`tw${j}`} html={w.html} title={w.title ?? undefined} />
              ))}
              {streamText ? <Bubble role="assistant" text={streamText} refs={flatRefs(turnTools)} onOpenDoc={(docId, page) => setDrawer({ docId, page })} /> : <TypingDots />}
              {turnSources.length > 0 && <WebSourceList sources={turnSources} />}
            </div>
          )}

          {voice.partialUser && <Bubble role="user" text={voice.partialUser} />}
          {voice.partialAssistant && <Bubble role="assistant" text={voice.partialAssistant} />}
          {!streaming && partialText && <Bubble role="assistant" text={partialText} />}
          {streaming && slow && <SlowNotice text={CHAT_SLOW_NOTICE} />}
          {error && (failedTurnRef.current && !streaming
            ? <RetryAlert message={error} partial={!!partialText} onRetry={retryTurn} />
            : <Alert>{error}</Alert>)}
        </div>
      </div>

      {/* Bulle de saisie flottante : jamais collée aux bords, ancrée en bas par
          le flux flex, grandit vers le haut avec le textarea. */}
      <form
        ref={formRef}
        onSubmit={send}
        className="[padding:12px_max(16px,env(safe-area-inset-right))_max(12px,env(safe-area-inset-bottom))_max(16px,env(safe-area-inset-left))] flex-none box-border"
      >
        <VoiceStatusBar voiceLive={voice.voiceLive} voiceState={voice.voiceState} maxWidth={760} />
        <div className="max-w-[760px] mx-auto">
          {pending.length > 0 && (
            <div className="flex gap-2 mb-[10px] flex-wrap">
              {pending.map((p) => (
                <PendingThumb key={p.id} att={p} onRemove={() => removePending(p.id)} />
              ))}
            </div>
          )}
          <div className="flex flex-col rounded-card border border-border bg-card shadow-card focus-within:border-primary/60">
            <input
              ref={imageInputRef}
              type="file"
              accept={IMAGE_MIMES.join(',')}
              multiple
              onChange={pickFiles}
              aria-label="Choisir un fichier"
              tabIndex={-1}
              className="hidden"
            />
            <input
              ref={docInputRef}
              type="file"
              accept={PDF_MIME}
              multiple
              onChange={pickFiles}
              aria-label="Choisir un fichier"
              tabIndex={-1}
              className="hidden"
            />
            <textarea
              ref={textareaRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onComposerKeyDown}
              rows={1}
              placeholder="Demandez à Naïa…"
              aria-label="Message"
              aria-keyshortcuts="Control+Enter Meta+Enter"
              disabled={streaming || voice.voiceLive}
              className="w-full max-h-[168px] resize-none border-none bg-transparent px-[14px] py-[11px] font-[inherit] text-[15px] text-foreground box-border focus:outline-none"
            />
            <div className="flex-none flex items-center gap-3 h-11 px-3 pb-3">
              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                disabled={streaming || voice.voiceLive}
                aria-label="Ajouter une photo"
                title="Ajouter une photo"
                className="flex-none w-11 h-11 grid place-items-center bg-card border border-border rounded-card cursor-pointer font-[inherit] text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ImageIcon size={19} weight="bold" />
              </button>
              <button
                type="button"
                onClick={() => docInputRef.current?.click()}
                disabled={streaming || voice.voiceLive}
                aria-label="Ajouter un PDF"
                title="Ajouter un PDF"
                className="flex-none w-11 h-11 grid place-items-center bg-card border border-border rounded-card cursor-pointer font-[inherit] text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FilePdf size={19} weight="bold" />
              </button>
              <div className="flex-1" />
              <VoiceMicButton voiceLive={voice.voiceLive} streaming={streaming} onToggle={voice.toggle} />
              <button
                type="submit"
                disabled={streaming || voice.voiceLive || (!draft.trim() && pending.length === 0)}
                aria-label="Envoyer"
                className="flex-none w-11 h-11 flex items-center justify-center p-0 bg-primary border-none rounded-card cursor-pointer font-[inherit] text-primary-foreground disabled:bg-accent disabled:text-muted-foreground disabled:cursor-not-allowed"
              >
                {streaming
                  ? <CircleNotch size={20} className="animate-spin" />
                  : <PaperPlaneTilt size={20} />}
              </button>
            </div>
          </div>
        </div>
          <p className="text-[11.5px] text-muted-foreground text-center mt-2">Réponses générées par une IA — vérifiez les informations importantes.</p>
      </form>

      {drawer && (
        <DocumentDrawer docId={drawer.docId} initialPage={drawer.page} onClose={() => setDrawer(null)} />
      )}
    </div>
  );
}

type OpenDoc = (docId: string, page: number) => void;

function flatRefs(tools?: ToolCall[]): DocRef[] {
  if (!tools?.length) return [];
  return tools.flatMap((t) => t.refs ?? []);
}

function WebSourceList({ sources }: { sources: WebSource[] }) {
  return (
    <div className="self-start w-full flex flex-col gap-[4px] rounded-sm border border-border bg-card px-3 py-[7px]">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--naia-text-secondary)]">
        Sources web
      </span>
      <ul className="flex flex-col gap-[3px]">
        {sources.map((s) => (
          <li key={s.uri} className="leading-snug">
            <a
              href={s.uri}
              target="_blank"
              rel="noreferrer noopener"
              className="text-[12px] text-[var(--naia-text-primary)] underline underline-offset-2 break-words min-h-[44px] sm:min-h-0 inline-flex items-center"
            >
              {s.title || s.domain || s.uri}
              {s.domain && s.title !== s.domain ? (
                <span className="ml-1 text-[var(--naia-text-secondary)]">({s.domain})</span>
              ) : null}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TruncationNotice() {
  return (
    <div className="self-start text-[12px] text-muted-foreground bg-accent border border-border rounded-sm px-3 py-[7px] leading-snug max-w-[88%]">
      ⚠️ Réponse tronquée — la demande était trop volumineuse. Posez des questions plus ciblées pour économiser vos crédits.
    </div>
  );
}

function MessageItem({ msg, onOpenDoc }: { msg: TeacherMsg; onOpenDoc: OpenDoc }) {
  const refs = flatRefs(msg.tools);
  if (msg.role === 'assistant' && (msg.tools?.length || msg.widgets?.length)) {
    return (
      <div className="self-start max-w-[88%] w-full flex flex-col gap-[6px]">
        <ToolBlockList tools={msg.tools ?? []} onOpenDoc={onOpenDoc} />
        {msg.widgets?.map((w, j) => (
          <WidgetFrame key={`w${j}`} html={w.html} title={w.title ?? undefined} />
        ))}
        <Bubble role="assistant" text={msg.content} refs={refs} onOpenDoc={onOpenDoc} />
        {msg.webSources?.length ? <WebSourceList sources={msg.webSources} /> : null}
        {msg.truncated && <TruncationNotice />}
      </div>
    );
  }
  if (msg.role === 'assistant') {
    return (
      <div className="self-start max-w-[88%] w-full flex flex-col gap-[6px]">
        <Bubble role="assistant" text={msg.content} refs={refs} onOpenDoc={onOpenDoc} />
        {msg.webSources?.length ? <WebSourceList sources={msg.webSources} /> : null}
        {msg.truncated && <TruncationNotice />}
      </div>
    );
  }
  return <Bubble role={msg.role} text={msg.content} attachments={msg.attachments} />;
}

function Bubble({
  role,
  text,
  refs,
  onOpenDoc,
  attachments,
}: {
  role: 'user' | 'assistant';
  text: string;
  refs?: DocRef[];
  onOpenDoc?: OpenDoc;
  attachments?: TeacherChatAttachment[];
}) {
  const isUser = role === 'user';
  return (
    <div className={cn(
      bubbleBase,
      isUser
        ? 'self-end bg-primary text-primary-foreground'
        : 'self-start bg-card border border-border shadow-card text-foreground',
    )}>
      <AttachmentList attachments={attachments} size="sm" tone="onPrimary" className="mb-2" />
      {isUser ? text : <Markdown text={text} refs={refs} onOpenDoc={onOpenDoc} />}
    </div>
  );
}
